import { FocusTask, Milestone, Booking, DbState } from '../types';
import { getBusinessSettings } from './businessSettings';

const TASKS_KEY = 'solostack_focus_tasks';
const MILESTONES_KEY = 'solostack_milestones';

export const DEFAULT_FOCUS_TASKS: FocusTask[] = [
  {
    id: 'ft-1',
    title: 'Follow up on unpaid client invoices & send direct payment links',
    category: 'revenue',
    priority: 'high',
    status: 'todo',
    dueDate: new Date(Date.now() + 86400000).toISOString().split('T')[0],
    revenueImpact: 1200,
    createdAt: new Date().toISOString()
  },
  {
    id: 'ft-2',
    title: 'Send Q3 Strategy & Consultation Proposal to new lead',
    category: 'sales',
    priority: 'high',
    status: 'in_progress',
    dueDate: new Date(Date.now() + 172800000).toISOString().split('T')[0],
    revenueImpact: 3500,
    createdAt: new Date().toISOString()
  },
  {
    id: 'ft-3',
    title: 'Review high-cost software subscriptions to maintain 50%+ profit margin',
    category: 'admin',
    priority: 'medium',
    status: 'todo',
    dueDate: new Date(Date.now() + 259200000).toISOString().split('T')[0],
    createdAt: new Date().toISOString()
  },
  {
    id: 'ft-4',
    title: 'Finalize client deliverable assets for active retainer project',
    category: 'product',
    priority: 'medium',
    status: 'done',
    dueDate: new Date(Date.now() - 86400000).toISOString().split('T')[0],
    createdAt: new Date().toISOString()
  }
];

export const DEFAULT_MILESTONES: Milestone[] = [
  {
    id: 'ms-1',
    title: 'Achieve $10,000 Monthly Revenue Milestone',
    targetRevenue: 10000,
    targetDate: '2026-08-31',
    status: 'in_progress',
    category: 'revenue'
  },
  {
    id: 'ms-2',
    title: 'Secure 5 Active Monthly Retainer Clients',
    targetDate: '2026-09-30',
    status: 'in_progress',
    category: 'growth'
  },
  {
    id: 'ms-3',
    title: 'Maintain 50%+ Safe Zone Profit Margin Across All Streams',
    targetDate: '2026-12-31',
    status: 'in_progress',
    category: 'operations'
  }
];

export function getFocusTasks(): FocusTask[] {
  try {
    const saved = localStorage.getItem(TASKS_KEY);
    if (saved !== null) {
      return JSON.parse(saved);
    }

    // Check if demo data is loaded in DB state
    const dbStateRaw = localStorage.getItem('solostack_db_state');
    if (dbStateRaw) {
      const parsed = JSON.parse(dbStateRaw) as DbState;
      const isDemo = parsed.bookings?.some((b) => b.id === 'b-1' || b.id === 'b-2' || b.id === 'b-3') ||
                     parsed.expenses?.some((e) => e.id === 'e-1' || e.id === 'e-2' || e.id === 'e-3');
      if (isDemo) {
        localStorage.setItem(TASKS_KEY, JSON.stringify(DEFAULT_FOCUS_TASKS));
        return DEFAULT_FOCUS_TASKS;
      }
    }
  } catch (e) {
    console.error('Failed to parse focus tasks from localStorage:', e);
  }
  return [];
}

export function saveFocusTasks(tasks: FocusTask[]): void {
  try {
    localStorage.setItem(TASKS_KEY, JSON.stringify(tasks));
  } catch (e) {
    console.error('Failed to save focus tasks:', e);
  }
}

export function getMilestones(): Milestone[] {
  try {
    const saved = localStorage.getItem(MILESTONES_KEY);
    if (saved !== null) {
      return JSON.parse(saved);
    }

    const dbStateRaw = localStorage.getItem('solostack_db_state');
    if (dbStateRaw) {
      const parsed = JSON.parse(dbStateRaw) as DbState;
      const isDemo = parsed.bookings?.some((b) => b.id === 'b-1' || b.id === 'b-2' || b.id === 'b-3') ||
                     parsed.expenses?.some((e) => e.id === 'e-1' || e.id === 'e-2' || e.id === 'e-3');
      if (isDemo) {
        localStorage.setItem(MILESTONES_KEY, JSON.stringify(DEFAULT_MILESTONES));
        return DEFAULT_MILESTONES;
      }
    }
  } catch (e) {
    console.error('Failed to parse milestones from localStorage:', e);
  }
  return [];
}

export function saveMilestones(milestones: Milestone[]): void {
  try {
    localStorage.setItem(MILESTONES_KEY, JSON.stringify(milestones));
  } catch (e) {
    console.error('Failed to save milestones:', e);
  }
}

export function clearFocusData(): void {
  try {
    localStorage.setItem(TASKS_KEY, JSON.stringify([]));
    localStorage.setItem(MILESTONES_KEY, JSON.stringify([]));
  } catch (e) {
    console.error('Failed to clear focus data:', e);
  }
}

export function loadDemoFocusData(): void {
  try {
    localStorage.setItem(TASKS_KEY, JSON.stringify(DEFAULT_FOCUS_TASKS));
    localStorage.setItem(MILESTONES_KEY, JSON.stringify(DEFAULT_MILESTONES));
  } catch (e) {
    console.error('Failed to load demo focus data:', e);
  }
}

// Auto-generates high-leverage suggestions based on real live bookings/invoices
export function getAutoRevenueSuggestions(bookings: Booking[], existingTasks: FocusTask[]): FocusTask[] {
  const suggestions: FocusTask[] = [];
  const existingTitles = new Set(existingTasks.map((t) => t.title.toLowerCase()));
  const currencySymbol = getBusinessSettings().currencySymbol || 'R';

  // 1. Unpaid Invoices
  const unpaidBookings = bookings.filter((b) => b.status === 'unpaid');
  unpaidBookings.forEach((b) => {
    const title = `Collect unpaid ${currencySymbol}${b.price.toLocaleString()} for ${b.client_name || 'Client'} (${b.invoice_number || `INV-${b.id}`})`;
    if (!existingTitles.has(title.toLowerCase())) {
      suggestions.push({
        id: `auto-unpaid-${b.id}`,
        title,
        category: 'revenue',
        priority: 'high',
        status: 'todo',
        revenueImpact: b.price,
        linkedInvoiceId: b.id,
        createdAt: new Date().toISOString()
      });
    }
  });

  return suggestions;
}
